import * as React from 'react';
import './_AsidePanel.scss';
import {
    CLASS_ASIDE_PANEL,
    CLASS_ASIDE_PANEL_DRAGGING,
    CLASS_ASIDE_PANEL_STATIC,
    CLASS_ASIDE_PANEL_STATIC_NO_HEADER
} from '../../constants';
import { AsidePanelHeader } from '../AsidePanelHeader/AsidePanelHeader';
import { AsidePanelBodyHOC } from '../../hocs/AsidePanelBodyHOC/AsidePanelBodyHOC';
import { AsidePanelContext } from './constants';
import { EAsidePanelProperty, IAsidePanelContext, IAsidePanelProps, TSubTab } from './types';
import { AsidePanelPrintStyle } from '../AsidePanelPrintStyle/AsidePanelPrintStyle';
import { root, vent } from 'app/store/constants';
import { AsidePanelFooter } from '../AsidePanelFooter/AsidePanelFooter';
import { MoveFocusInside } from 'react-focus-lock';
import { CLASS_ASIDE_PANEL_ACTIONS_SCROLL } from '../AsidePanelActions/constants';
import Draggable, { DraggableData, DraggableEvent } from 'react-draggable';
import { ASIDE_MAX_WIDTH, ASIDE_MIN_WIDTH, COMMENTS_COLUMN_VIEW_MIN_WIDTH, GOOGLE_SPACING } from 'app/const';
import { v4 as uuidv4 } from 'uuid';
import { CLASS_ASIDE_PANEL_BODY } from '../AsidePanelBody/constants';
import { HEADER_HEIGHT } from '../AsidePanelHeader/constants';
import { getParentsClasses } from 'app/view/react_components/helpers/getParentsClasses';
import {
    ID_ASIDE_PANEL_ACTIONS_PORTAL_MOBILE,
    ID_ASIDE_PANEL_DATEPICKER_PORTAL
} from '../AsidePanelActionsButton/constants';
import { SegmentCardDetailsCloseSourceValue } from '../../../../../../middlewares/segment/trackEntities/cardEvents';
import { AnalyzeRender } from 'app/view/react_components/helpers/memoizeHelper';
import { isEqual } from 'underscore';
import { ASIDE_PANE_COMMENTS_COLUMN_ANIMATION } from '../AsidePanelCommentsColumnViewButton/constants';

//@ts-ignore
const analyzeRender = window.Settings.development ? new AnalyzeRender(`AsidePanel_container`) : null;

export function AsidePanel ({
    activeTabIndex,
    asidePanelWidth,
    boardId,
    cardId,
    listId,
    children,
    className,
    closedRef,
    columnWidth,
    filterId,
    footer,
    isActionsScticky,
    isActionsMinimized,
    isActionsScrollWithBodyDisabled,
    isAllowColumnsView,
    isButtonsFixed,
    isColumnsView,
    isFakeRequests,
    isLeft,
    isLoading,
    isMobile,
    isPrint,
    isRequests,
    isUserTimeFormat12,
    mobileButton,
    isAssignedToMe,
    isAssignedToMeWork,
    isCardFromNotification,
    tabs,
    title,
    titleAction,
    type,
    isForceClosed,
    subTab,
    onClose,
    onTabActive,
    onDidMount,
    onDidUnmount,
    onResize,
    onResized,
    panelActions,
    onSetColumnView,
    onSubTabChange,
}: IAsidePanelProps) {
    if (analyzeRender) analyzeRender.call(`${boardId} ${listId} ${cardId}`);

    const [activeHook, setActiveHook] = React.useState(activeTabIndex);
    const [isLoadingHook, setIsLoading] = React.useState(isLoading);
    const [isLoaded, setIsLoaded] = React.useState(false);
    const [isWithoutHeader, setWithoutHeader] = React.useState(false);
    let [isCommentsColumn, setCommentsColumn] = React.useState(false);
    const [s] = React.useState<any>({}); // положить getActiveHook в useState, чтобы сhangeTab из контекста получил актуальный activeHook
    s.activeHook = activeHook;

    const panelRef = React.useRef(null);

    const classNamePanel = CLASS_ASIDE_PANEL;
    const classNamePanelPrint = isPrint ? CLASS_ASIDE_PANEL + '--print' : '';
    const classNamePanelMobile = isMobile ? CLASS_ASIDE_PANEL + '--mobile' : '';
    const classNamePanelFooter = footer ? CLASS_ASIDE_PANEL + '--footer' : '';
    const classNameActionsMinimized = isActionsMinimized && !isButtonsFixed ? CLASS_ASIDE_PANEL + '--actions-minimized' : '';
    const classNameWithoutHeader = isWithoutHeader ? CLASS_ASIDE_PANEL + '--without-header' : '';
    const classNamePanelResizer = classNamePanel + '__resizer';

    const addShowedProperty = React.useCallback((
        property: EAsidePanelProperty,
        isCommentsColumn: boolean, // сбрасывается на старое значение
    ) => {
        const showedProperties = new Set([...context.showedProperties]) as Set<EAsidePanelProperty>;
        showedProperties.add(property);
        const contextNew = {
            ...context,
            showedProperties,
            isCommentsColumn,
        };
        // showedProperty обновляем всегда т.к. set не проходит проверку в isEqual
        // напрямую showedProperty менять тоже нельзя, иначе компоненты обновляются с большим лагом
        context = contextNew;
        _setContext(contextNew);
    }, []);

    const setSubTab = React.useCallback((
        subTab: TSubTab,
    ) => {
        setContext({ subTab });
        if (onSubTabChange) onSubTabChange(subTab);
    }, []);

    const onLoaded = React.useCallback((
        key: string, isLoading: boolean
    ) => {
        requestAnimationFrame(() => setIsLoading(!isLoading));
    }, []);

    const onTabChange = React.useCallback((
        activeTabIndex: number
    ) => {
        setActiveHook(activeTabIndex);
        if (onTabActive) onTabActive(activeTabIndex);
    }, []);

    const changeTab = React.useCallback((
        tabKey: string
    ) => {
        const activeHook = s.activeHook;
        if (tabs[activeHook].key === tabKey) return;

        const tabIndex = tabs.findIndex(tab => tab.key === tabKey);
        onTabChange(tabIndex);
    }, []);

    const setIsScrollDisabled = React.useCallback((
        isScrollDisabled: boolean
    ) => {
        setContext({isScrollDisabled});
    }, []);

    const setOpenedProperty = React.useCallback((
        openedProperty: EAsidePanelProperty,
        isMobile: boolean, // сбрасывается на старое значение
        isCommentsColumn: boolean, // сбрасывается на старое значение
    ) => {
        setContext({ openedProperty, isMobile, isCommentsColumn });
    }, []);

    let [context, _setContext] = React.useState({
        boardId,
        cardId,
        listId,
        filterId,
        isButtonsFixed,
        isCommentsColumn,
        isMobile,
        isUserTimeFormat12,
        mobileButton,
        type,
        tabs,
        showedProperties: new Set,
        changeTab,
        setIsScrollDisabled,
        setOpenedProperty,
        onLoaded,
        addShowedProperty,
        asidePanelWidth,
        setSubTab,
        subTab,
    } as IAsidePanelContext);
    function setContext (
        value: IAsidePanelContext
    ) {
        const contextNew = {
            ...context,
            ...value
        };
        if (!isEqual(contextNew, context)) {
            context = contextNew;
            _setContext(contextNew);
        }
    }

    const isCommentsColumnDebounce = React.useRef(null);
    React.useEffect(() => {
        if (!cardId) return;

        const _setCommentsColumn = () => {
            isCommentsColumn = isColumnsView && root._innerWidth > COMMENTS_COLUMN_VIEW_MIN_WIDTH;
            setCommentsColumn(isCommentsColumn);
            vent.trigger(vent['view:resized']);
        };

        const onResize = () => {
            if (isCommentsColumnDebounce.current) clearTimeout(isCommentsColumnDebounce.current);
            isCommentsColumnDebounce.current = setTimeout(_setCommentsColumn, 100);
        };

        _setCommentsColumn();

        root.addEventListener('resize', onResize);
        return () => {
            root.removeEventListener('resize', onResize);
            if (isCommentsColumnDebounce.current) clearTimeout(isCommentsColumnDebounce.current);
        }
    }, [isColumnsView]);

    const resizer = React.useRef({
        actionsWidth: 0,
        columnWidth: 0,
        minWidth: ASIDE_MIN_WIDTH,
        maxWidth: ASIDE_MAX_WIDTH,
        width: 0,
        x: 0,
    });
    const resizerRef = React.useRef(null);
    const preventResizeRef = React.useRef(null);
    const [resizerKey, setResizerKey] = React.useState('resizer-key');

    const fixColumn = () => {
        resizer.current.width = panelRef.current && panelRef.current.offsetWidth || 0;
        if (resizerRef.current) resizerRef.current.style.right = resizer.current.width;
        setResizerKey(uuidv4());
    };

    const onMouseMove = React.useCallback(({ x }: MouseEvent) => {
        if (preventResizeRef.current) return;
        const hideOffset = GOOGLE_SPACING * 14;
        const innerWidth = root._innerWidth;
        const { actionsWidth, columnWidth } = resizer.current;
        if (
            !isCommentsColumn && innerWidth - x > ASIDE_MAX_WIDTH + actionsWidth + hideOffset ||
            isCommentsColumn && x > innerWidth - ASIDE_MIN_WIDTH - columnWidth - actionsWidth - GOOGLE_SPACING + hideOffset
        ) {
            preventResizeRef.current = true;
            setTimeout(() => {
                onSetColumnView(!isCommentsColumn);
            }, 0);
            setTimeout(() => {
                preventResizeRef.current = false;
            }, 200);
            fixColumn();
            if (isCommentsColumn) {
                setTimeout(() => {
                    handleResizerDragStop(null, null, ASIDE_MAX_WIDTH);
                }, 100);
            } else {
                handleResizerDragStop(null, null, ASIDE_MIN_WIDTH);
            }
        }
    }, [isCommentsColumn]);

    const handleResizerDragStart = () => {
        if (isAllowColumnsView && root._innerWidth > COMMENTS_COLUMN_VIEW_MIN_WIDTH) {
            document.addEventListener('mousemove', onMouseMove);
        }
        const aside = document.querySelector('.' + CLASS_ASIDE_PANEL) as HTMLElement;
        if (aside) {
            aside.classList.add(CLASS_ASIDE_PANEL_DRAGGING);
        }
    };

    const resizerDebounce = React.useRef(null);
    let resizerTime = React.useRef(0);
    const _handleResizerDrag = (
        draggableData: DraggableData,
    ) => {
        resizer.current.x = draggableData.x;
        const asideWidth = resizer.current.width - draggableData.x;
        const content = document.querySelector('.content__inner') as HTMLElement;
        if (!content || preventResizeRef.current) return;
        const w = Math.min(asideWidth, content.offsetWidth - GOOGLE_SPACING * 4);
        root.App.controller.mainView.setAsidePanelWidth(w + 'px');
        resizerTime.current = Date.now();
    };

    const handleResizerDrag = (
        e: DraggableEvent,
        draggableData: DraggableData,
    ) => {
        const delay = 100;
        if (resizerDebounce.current) clearTimeout(resizerDebounce.current);
        if (Date.now() - resizerTime.current > delay) {
            _handleResizerDrag(draggableData);
        } else {
            resizerDebounce.current = setTimeout(() => _handleResizerDrag(draggableData), delay);
        }
    };

    const handleResizerDragStop = (
        e: DraggableEvent,
        draggableData: DraggableData,
        width?: number,
    ) => {
        document.removeEventListener('mousemove', onMouseMove);
        const aside = document.querySelector('.' + CLASS_ASIDE_PANEL) as HTMLElement;
        if (aside) {
            aside.classList.remove(CLASS_ASIDE_PANEL_DRAGGING);
        }
        if (resizerDebounce.current) clearTimeout(resizerDebounce.current);
        if (width) {
            if (onResized) onResized(width);
        } else if (resizer.current.x) {
            resizer.current.width -= resizer.current.x;
            resizer.current.x = 0;
            setResizerKey(uuidv4());
            if (onResized) onResized(resizer.current.width - resizer.current.actionsWidth - resizer.current.columnWidth);
        }
    };

    const onMouseDown = () => {// to keep asidePanel open if onMouseUp is outside asidePanel
        root.App.controller.setAsidePanelMouseDown(true);
    };
    React.useEffect(() => {
        requestAnimationFrame(() => {
            const asidePanelActions = panelRef.current && panelRef.current.querySelector('.' + CLASS_ASIDE_PANEL_ACTIONS_SCROLL) as HTMLElement;
            if (asidePanelActions) {
                resizer.current.actionsWidth = asidePanelActions.offsetWidth;
            }
            let _columnWidth = isCommentsColumn ? columnWidth : 0;
            resizer.current.columnWidth = _columnWidth;
            if (_columnWidth || asidePanelActions) {
                let actionsWidth = 0;
                if (asidePanelActions) {
                    actionsWidth = asidePanelActions.offsetWidth;
                }
                resizer.current.minWidth = ASIDE_MIN_WIDTH + actionsWidth + _columnWidth;
                resizer.current.maxWidth = ASIDE_MAX_WIDTH + actionsWidth + _columnWidth;
            }

            setTimeout(() => { // не успевает ширину поменять при переключение column
                fixColumn();
            }, ASIDE_PANE_COMMENTS_COLUMN_ANIMATION);
        });
    }, [
        className,
        isActionsMinimized,
        columnWidth,
        isCommentsColumn,
    ]);

    React.useEffect(() => {
        const body = panelRef.current && panelRef.current.querySelector('.' + CLASS_ASIDE_PANEL_BODY) as HTMLElement;
        let scrollTop = 0;
        let isWithoutHeader = false;
        let blocker: number;

        function _setWithoutHeader(is: boolean) {
            if (isWithoutHeader === is) return;

            isWithoutHeader = is;
            setWithoutHeader(is);
            if (blocker) clearTimeout(blocker);
            blocker = root.setTimeout(() => { // блокер для onScroll, пока меняется состояние хэдера
                blocker = null;
            }, 300);
        }

        function onScroll () {
            const asidePanel = document.querySelector('.' + CLASS_ASIDE_PANEL) as HTMLElement;
            if (
                blocker ||
                (
                    asidePanel && (
                        asidePanel.classList.contains(CLASS_ASIDE_PANEL_STATIC_NO_HEADER) ||
                        asidePanel.classList.contains(CLASS_ASIDE_PANEL_STATIC)
                    )
                )
            ) {
                return;
            }

            if (body.scrollTop < scrollTop) { // при скроле наверх сразу возвращать хэдер
                _setWithoutHeader(false);
            } else if (
                !isWithoutHeader &&
                body.scrollTop > HEADER_HEIGHT * 2 // прятать хэдер, когда прокрутили больше его высоты
            ) {
                _setWithoutHeader(true);
            }
            scrollTop = body.scrollTop;
        }

        if (isActionsScticky) {
            if (body) {
                body.addEventListener('scroll', onScroll);
            }
        } else {
            _setWithoutHeader(false);
            if (body) body.removeEventListener('scroll', onScroll);
        }

        return ()=>{
            if (body) body.removeEventListener('scroll', onScroll);
            if (blocker) clearTimeout(blocker);
        }
    }, [isActionsScticky]);

    React.useEffect(() => {
        setContext({
            filterId,
            isCommentsColumn,
            isMobile,
            isUserTimeFormat12,
            tabs,
            asidePanelWidth
        });
    }, [
        filterId,
        isCommentsColumn,
        isMobile,
        isUserTimeFormat12,
        tabs,
        activeTabIndex,
        asidePanelWidth
    ]);

    React.useEffect(() => {
        onTabChange(activeTabIndex);
    }, [activeTabIndex]);

    React.useEffect(() => {
        setIsLoading(isLoading);
    }, [isLoading]);

    React.useEffect(() => {
        function onResizeWindow () {
            if (onResize) onResize(root._innerWidth);
        };

        if (onDidMount) {
            onDidMount().then(() => {
                setIsLoaded(true);
            });
        } else {
            setIsLoaded(true);
        }
        if (onResize) {
            requestAnimationFrame(onResizeWindow);
            root.removeEventListener('resize', onResizeWindow);
            root.addEventListener('resize', onResizeWindow);
        }

        function onEsc (e: KeyboardEvent) {
            if (e.key === 'Escape') {
                const target = e.target as HTMLElement;
                const targetClasses = getParentsClasses(
                    target, [
                        'kui-select-list__item', // закрыть дропдаун
                        'kui-input', // убрать фокус с инпута
                        CLASS_ASIDE_PANEL,
                    ]
                );
                if (targetClasses.includes('kui-select-list__item')) return;

                if (targetClasses.includes('kui-input')) {
                    const closestTabIndex = target.closest('[tabindex]') as HTMLElement;
                    if (closestTabIndex) closestTabIndex.focus();
                    return;
                }
                if (!document.activeElement.classList.contains('picker-dialog')) {
                    onClose(SegmentCardDetailsCloseSourceValue.ESC_BUTTON)
                }
            }
        };
        root.addEventListener('keydown', onEsc);

        return () => {
            if (onDidUnmount) onDidUnmount();
            if (onResize) {
                root.removeEventListener('resize', onResizeWindow);
            }
            if (
                closedRef &&
                document.body === document.activeElement // если фокус "нигде", вернуть инициатору панели
            ) {
                closedRef.focus();
            }
            root.removeEventListener('keydown', onEsc);
        }
    }, []);

    const ResizerSvg = require('!!@svgr/webpack!../../../../../../../../../assets/svg/spreader.svg').default;

    const content = (
        <AsidePanelContext.Provider value={context}>
            <div
                className={`
                    ${classNamePanel}
                    ${className || ''}
                    ${classNamePanelPrint}
                    ${classNamePanelMobile}
                    ${classNamePanelFooter}
                    ${classNameActionsMinimized}
                    ${classNameWithoutHeader}
                    ${isCommentsColumn ? CLASS_ASIDE_PANEL + '--columns' : ''}
                    ${tabs ? classNamePanel + '--' + tabs[activeTabIndex || 0].key.toLowerCase() : ''}
                `}
                ref={panelRef}
                onMouseDown={onMouseDown}
                tabIndex={-1}
            >
                {isLoadingHook &&
                    <div className={classNamePanel + '__loader'} />
                }
                <AsidePanelHeader
                    activeTabIndex={activeHook}
                    isLoading={isLoadingHook || isRequests}
                    isFakeLoading={isFakeRequests}
                    tabs={tabs}
                    title={title}
                    titleAction={titleAction}
                    isForceClosed={isLoaded && isForceClosed}
                    onClose={onClose}
                    onTabChange={onTabChange}
                    isAssignedToMe={isAssignedToMe}
                    isAssignedToMeWork={isAssignedToMeWork}
                    isCardFromNotification={isCardFromNotification}
                />
                <AsidePanelBodyHOC
                    boardId={boardId}
                    activeTabIndex={activeHook}
                    children={children}
                    tabs={tabs}
                    panelActions={panelActions}
                    isActionsScrollWithBodyDisabled={isActionsScrollWithBodyDisabled}
                    isAssignedToMe={isAssignedToMe}
                    cardId={cardId}
                />
                {!!footer &&
                    <AsidePanelFooter footer={footer} />
                }
                {!isMobile &&
                    <Draggable
                        key={resizerKey}
                        axis={'y'}
                        bounds={{
                            left: resizer.current.width - resizer.current.maxWidth,
                            right: resizer.current.width - resizer.current.minWidth,
                            top: 0,
                            bottom: 0,
                        }}
                        onStart={handleResizerDragStart}
                        onDrag={handleResizerDrag}
                        onStop={handleResizerDragStop}
                    >
                        <div className={classNamePanelResizer} ref={resizerRef}>
                            <ResizerSvg />
                        </div>
                    </Draggable>
                }
                {!isLeft &&
                    <div id={ID_ASIDE_PANEL_ACTIONS_PORTAL_MOBILE}>
                        <div id={ID_ASIDE_PANEL_DATEPICKER_PORTAL} />
                    </div>
                }
            </div>
            <AsidePanelPrintStyle/>
        </AsidePanelContext.Provider>
    );

    return <MoveFocusInside>{content}</MoveFocusInside>;
};
